import type { Color } from '@pz/scene';
import {
  createColorBlock,
  openFontPicker,
  type ColorBlockHandle,
  type ColorBlockOptions,
  type FontPickerHandle,
  type FontPickerOptions,
} from '../controls.js';
import { el, field, searchBox, swatchGrid } from '../panels.js';

/**
 * The shared pickers, with a floor under them.
 *
 * `controls.ts` is written by the desktop agent and its bodies throw until
 * that lands. The phone shell is built and verified in parallel, so every use
 * goes through these wrappers: when the real control exists the phone gets it
 * (one colour block, one font picker for both shells, which is the whole point
 * of that file), and until then it falls back to the plain swatch grid and
 * font list below rather than throwing inside a sub-panel.
 *
 * Remove the fallbacks only when `controls.ts` has shipped and the desktop
 * behaviour is what we want on a phone too.
 */

export function safeColorBlock(options: ColorBlockOptions): ColorBlockHandle {
  try {
    return createColorBlock(options);
  } catch {
    return fallbackColorBlock(options);
  }
}

export function safeFontPicker(options: FontPickerOptions): FontPickerHandle {
  try {
    return openFontPicker(options);
  } catch {
    return fallbackFontPicker(options);
  }
}

/** Title, swatches, the browser's own colour wheel — nothing more. */
function fallbackColorBlock(options: ColorBlockOptions): ColorBlockHandle {
  const ctx = options.ctx;
  const box = options.title ? field(options.title) : { wrap: el('div', 'pzd-field') };
  const host = el('div');
  box.wrap.append(host);
  let selected = options.value;

  const paint = (): void => {
    host.textContent = '';
    const palette = options.palette ?? ctx.config.text.palette;
    const allowCustom = options.allowCustom ?? ctx.config.text.allowCustomColor;
    host.append(swatchGrid({
      colors: palette,
      ...(selected ? { selected } : {}),
      onPick: (color: Color) => {
        selected = color;
        paint();
        options.onPick(color);
      },
      ...(options.allowNone
        ? {
          none: {
            label: ctx.texts.none,
            onPick: () => {
              selected = undefined;
              paint();
              options.onPick(undefined);
            },
          },
        }
        : {}),
      ...(allowCustom ? { custom: { label: ctx.texts.customColor } } : {}),
    }));
  };
  paint();

  return {
    element: box.wrap,
    setValue(color) {
      selected = color;
      paint();
    },
    destroy: () => box.wrap.remove(),
  };
}

/**
 * A searchable list of families, each row drawn in its own face. The faces are
 * fetched lazily (a zone may offer two hundred), and a failed fetch simply
 * leaves the row in the fallback family instead of blanking it.
 */
function fallbackFontPicker(options: FontPickerOptions): FontPickerHandle {
  const ctx = options.ctx;
  const wrap = el('div', 'pzd-ph-font-picker');
  const list = el('div', 'pzd-ph-list');
  let query = '';

  const paint = (): void => {
    list.textContent = '';
    const needle = query.trim().toLocaleLowerCase('sr-RS');
    const fonts = ctx.fonts.list.filter((font) => !needle
      || font.family.toLocaleLowerCase('sr-RS').includes(needle)
      || font.style.toLocaleLowerCase('sr-RS').includes(needle));
    if (!fonts.length) {
      list.append(el('div', 'pzd-hint', ctx.texts.noResults));
      return;
    }
    for (const font of fonts.slice(0, 120)) {
      const row = el('button', 'pzd-ph-list-btn');
      row.type = 'button';
      const name = `${font.family}${font.style && font.style !== 'regular' ? ` ${font.style}` : ''}`;
      row.textContent = name;
      row.setAttribute('aria-label', name);
      row.setAttribute('aria-pressed', String(font.id === options.fontId));
      if (font.id === options.fontId) row.classList.add('is-on');
      row.style.fontFamily = `${ctx.fonts.cssFamily(font.id)}, sans-serif`;
      row.style.fontSize = '17px';
      void ctx.fonts.ensure(font.id).catch(() => undefined);
      row.addEventListener('click', () => options.onPick(font.id));
      list.append(row);
    }
  };

  wrap.append(searchBox(ctx.texts.search, '', (value) => {
    query = value;
    paint();
  }));
  wrap.append(list);
  paint();
  options.host.append(wrap);

  return {
    element: wrap,
    destroy: () => wrap.remove(),
  };
}
